import React, { useEffect, useRef, useState } from 'react';
import { CircleAlertIcon, Icon, LoadingSkeleton } from 'smoothie';

import { getAuthorizationHeader } from '../../../../auth/stytchHelpers';
import { captureException } from '../../../../logger/Logger';
import type { CardDataProps } from '../../../types/Card';
import { buildMarqetaCardDataUrl, injectMarqetaScripts } from './marqetaUtils';

const CardData = ({ cardDataType, cardId, onCopy }: CardDataProps) => {
    const [html, setHtml] = useState<string>('');
    const [isFetching, setIsFetching] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const uri = buildMarqetaCardDataUrl({ cardId, cardDataType });

    // See copy-card-pan.html in backend
    // A direct window reference is used to pass data back from the Marqeta iFrame
    // @ts-expect-error: window type is not aware of webCardWidgetOnCopy from copy-card-pan.html in backend
    if (window?.webCardWidgetOnCopy == null && onCopy) {
        // @ts-expect-error: window type is not aware of webCardWidgetOnCopy from copy-card-pan.html in backend
        window.webCardWidgetOnCopy = {};
    }
    if (onCopy) {
        // @ts-expect-error: window type is not aware of webCardWidgetOnCopy from copy-card-pan.html in backend
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        window.webCardWidgetOnCopy[cardId] = onCopy;
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                const headers = getAuthorizationHeader();
                const response = await fetch(uri, { headers });
                const data = await response.text();
                setHtml(data);
                setError(null);
            } catch (error) {
                captureException(
                    new Error(
                        'CardDetails:CardData(web): Error fetching card data:',
                        { cause: error },
                    ),
                );
                setHtml(''); // Clear any previous data
                setError('error');
            } finally {
                setIsFetching(false);
            }
        };

        void fetchData();
    }, [cardDataType, cardId]);

    useEffect(() => {
        if (!isFetching && error == null && html && containerRef.current) {
            injectMarqetaScripts({
                html,
                containerRef,
                error,
            });
        }
    }, [isFetching, error, html]);

    if (error != null) {
        return <Icon icon={CircleAlertIcon} color="$red1000" />;
    }

    return (
        <>
            <div ref={containerRef} />
            {isFetching && <LoadingSkeleton position="absolute" />}
        </>
    );
};

export default CardData;
